<template>
  <main>
    <section class="flex h-full flex-col overflow-hidden relative" v-if="!showThanks">
      <loading
        :active.sync="loading"
        color="#556ee6"
        loader="dots"
        :can-cancel="false"
        :is-full-page="false"
      />

      <div class="flex-1">
        <!-- Header -->
        <div class="mb-8">
          <!-- Step bar (wizard mode) -->
          <div v-if="isWizardMode && showForm" class="flex items-center justify-between border-b border-gray-300 gap-2 md:gap-4 overflow-hidden">
            <div
              v-for="(step, idx) in wizardSteps"
              :key="step.id || idx"
              class="flex-1 text-center py-2 transition-all duration-300 px-2 min-w-0"
              :class="{
                'text-blue-600 border-b-2 border-blue-600 font-medium': wizardStep === idx + 1,
                'text-gray-500': wizardStep !== idx + 1
              }"
            >
              <span class="truncate block">
                <span class="hidden md:inline">{{ idx + 1 }}. {{ step.name || 'Etapa ' + (idx + 1) }}</span>
                <span class="md:hidden">{{ idx + 1 }}<template v-if="wizardStep === idx + 1">. {{ step.name || 'Etapa ' + (idx + 1) }}</template><template v-else>.</template></span>
              </span>
            </div>
          </div>
          <!-- Header simples (flat mode / antes do form carregar) -->
          <div v-else class="text-center py-4 border-b border-gray-300">
            <span class="text-gray-900 font-regular">
              {{ rule.name || 'AÇÃO PENDENTE' }}
            </span>
          </div>
        </div>

        <!-- Erro -->
        <div v-if="error" class="text-center py-12 px-8">
          <svg class="mx-auto h-16 w-16 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h2 class="text-xl font-semibold text-gray-800 mb-2">{{ errorTitle }}</h2>
          <p class="text-gray-500">{{ errorMessage }}</p>
        </div>

        <!-- Saudacao + Form -->
        <div v-if="showForm">
          <div class="text-center mb-6 px-8">
            <h3 class="text-lg font-semibold text-gray-800">Olá, {{ participant.name }}!</h3>
            <p v-if="rule.description" class="text-gray-500 mt-1 text-sm">{{ rule.description }}</p>
            <p v-else class="text-gray-500 mt-1 text-sm">Preencha o formulário abaixo para concluir sua ação pendente.</p>
          </div>

          <!-- ===================== -->
          <!-- FLAT MODE (sem steps) -->
          <!-- ===================== -->
          <form v-if="!isWizardMode" @submit.prevent="handleSubmit" class="form md:px-8 mb-4" autocomplete="off">
            <div class="space-y-4">
              <div class="flex flex-wrap -mx-2">
                <template v-for="field in allVisibleFields" :key="field.id || field.name">
                  <!-- Section Divider -->
                  <div v-if="field.type === 'section' || field.component === 'SectionDivider'" class="w-full mb-2 mt-6 px-2">
                    <div class="text-md font-normal">{{ field.label }}</div>
                    <hr class="mt-2" />
                  </div>

                  <!-- Campo -->
                  <div v-else :class="getFieldContainerClass(field)">
                    <div v-if="!isCheckboxField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field)" class="mb-2 text-sm">
                      {{ field.label }}
                      <span v-if="field.required" class="text-red-500">*</span>
                      <a
                        v-if="field.label_link_text && field.label_link_url"
                        :href="field.label_link_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ml-2 text-xs text-blue-600 underline hover:text-blue-800"
                      >{{ field.label_link_text }}</a>
                    </div>

                    <!-- Phone -->
                    <input v-if="isPhoneField(field)" type="tel" v-model="formData[field.name]" :placeholder="field.placeholder || '(00) 0000-0000'" v-maska:[phoneMaskOpts] :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- CPF -->
                    <input v-else-if="isCpfField(field)" type="text" v-model="formData[field.name]" :placeholder="field.placeholder || '000.000.000-00'" v-maska :data-maska="field.mask || '###.###.###-##'" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- CNPJ -->
                    <input v-else-if="isCnpjField(field)" type="text" v-model="formData[field.name]" :placeholder="field.placeholder || '00.000.000/0000-00'" v-maska :data-maska="field.mask || '##.###.###/####-##'" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- CEP -->
                    <input v-else-if="isCepField(field)" type="text" v-model="formData[field.name]" :placeholder="field.placeholder || '00000-000'" v-maska :data-maska="field.mask || '#####-###'" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @input="onFieldInput(field)" @blur="touchField(field.name)" />

                    <!-- Multiselect -->
                    <IMultiselect v-else-if="isMultiselectField(field)" v-model="formData[field.name]" :options="getFieldOptions(field)" :placeholder="field?.placeholder || 'Selecione'" :disabled="isFieldDisabled(field)" :filter="field.config?.searchable !== false" :error="hasFieldError(field)" @change="touchField(field.name)" />

                    <!-- Select (estilizado) -->
                    <ISelect v-else-if="isSelectField(field)" :modelValue="formData[field.name]" @update:modelValue="val => { formData[field.name] = val; touchField(field.name) }" :options="getFieldOptions(field)" :placeholder="field?.placeholder || 'Selecione'" :disabled="isFieldDisabled(field)" :error="hasFieldError(field)" />

                    <!-- Radio -->
                    <div v-else-if="isRadioField(field)" class="flex gap-4 flex-wrap">
                      <label v-for="opt in getFieldOptions(field)" :key="opt?.value || opt" class="flex items-center cursor-pointer">
                        <input type="radio" :name="field.name" :value="opt?.value || opt" v-model="formData[field.name]" class="mr-2 custom-radio" :class="{ 'border-red-500': hasFieldError(field) }" @change="touchField(field.name)" />
                        <span class="text-sm">{{ opt?.label || opt }}</span>
                      </label>
                    </div>

                    <!-- Checkbox / Terms -->
                    <label v-else-if="isCheckboxField(field)" class="flex items-start cursor-pointer">
                      <input type="checkbox" v-model="formData[field.name]" class="mr-2 mt-1 custom-checkbox" @change="touchField(field.name)" />
                      <span class="text-sm text-gray-700" v-html="renderCheckboxLabel(field)"></span>
                    </label>

                    <!-- File Upload -->
                    <div v-else-if="isFileField(field)" class="upload-box" @click="triggerFileInput(field.name)">
                      <input type="file" :ref="el => { if (el) fileRefs[field.name] = el }" class="hidden" :accept="field.config?.accept || '*/*'" @change="handleFileChange(field.name, $event)" />
                      <div v-if="!files[field.name]" class="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors">
                        <div class="flex justify-center mb-2">
                          <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
                        </div>
                        <p class="text-gray-600">Arraste e solte ou <span class="text-blue-500">clique para enviar</span></p>
                        <p class="text-sm text-gray-500 mt-1">{{ field.help_text || 'Anexe o arquivo solicitado' }}</p>
                      </div>
                      <div v-else class="text-center py-4 border-2 border-green-300 rounded-lg bg-green-50">
                        <p class="text-green-600">Arquivo selecionado: {{ files[field.name].name }}</p>
                        <span class="text-blue-500 cursor-pointer text-sm" @click.stop="clearFile(field.name)">Alterar</span>
                      </div>
                    </div>

                    <!-- Textarea -->
                    <textarea v-else-if="isTextareaField(field)" v-model="formData[field.name]" :placeholder="field.placeholder" :maxlength="getFieldMaxLength(field) || undefined" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" rows="3" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Date -->
                    <input v-else-if="isDateField(field)" type="text" inputmode="numeric" v-maska data-maska="##/##/####" placeholder="DD/MM/AAAA" v-model="formData[field.name]" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Date + Time -->
                    <input v-else-if="isDatetimeField(field)" type="text" inputmode="numeric" v-maska data-maska="##/##/#### ##:##" placeholder="DD/MM/AAAA HH:MM" v-model="formData[field.name]" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Terms Acceptance -->
                    <i-terms-acceptance
                      v-else-if="isTermsAcceptanceField(field)"
                      v-model="formData[field.name]"
                      :label="''"
                      :config="field.config || {}"
                      :hasError="hasFieldError(field)"
                      :errorMessage="getFieldErrorMessage(field)"
                      :disabled="isFieldDisabled(field)"
                      :required="field.required"
                    />

                    <!-- Fake Ticket (display-only) -->
                    <i-fake-ticket
                      v-else-if="isFakeTicketField(field)"
                      :config="field.config || {}"
                    />

                    <!-- Disclaimer (display-only) -->
                    <i-disclaimer
                      v-else-if="isDisclaimerField(field)"
                      :config="field.config || {}"
                    />

                    <!-- Instruções (display-only ou com aceite) -->
                    <i-instrucoes
                      v-else-if="isInstrucoesField(field)"
                      :modelValue="formData[field.name] === true"
                      @update:modelValue="val => { formData[field.name] = val; touchField(field.name); }"
                      :config="field.config || {}"
                      :hasError="hasFieldError(field)"
                    />

                    <!-- Default Text Input -->
                    <input v-else :type="getInputType(field)" v-model="formData[field.name]" :placeholder="field.placeholder" :maxlength="getFieldMaxLength(field) || undefined" v-maska :data-maska="field.mask || undefined" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Error -->
                    <span v-if="hasFieldError(field) && !isTermsAcceptanceField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field)" class="text-red-500 text-xs mt-1 block">{{ getFieldErrorMessage(field) }}</span>

                    <!-- Help text -->
                    <p v-if="field.help_text && !isFileField(field) && !isCheckboxField(field) && !isTermsAcceptanceField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field) && !hasFieldError(field)" class="text-xs text-gray-500 mt-1">{{ field.help_text }}</p>
                  </div>
                </template>
              </div>
            </div>

            <div class="flex flex-col-reverse md:flex-row justify-between mt-8 gap-4">
              <button type="submit" :disabled="isSubmitting" class="button-subs w-full text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50">
                {{ isSubmitting ? 'Enviando...' : submitButtonText }}
              </button>
            </div>
          </form>

          <!-- ======================== -->
          <!-- WIZARD MODE (com steps)  -->
          <!-- ======================== -->
          <form v-if="isWizardMode" @submit.prevent="handleSubmit" class="form md:px-8 mb-4" autocomplete="off">
            <div
              v-for="(step, stepIndex) in visibleSteps"
              :key="step.id || stepIndex"
              v-show="wizardStep === stepIndex + 1"
              class="space-y-4"
            >
              <p v-if="step.description" class="text-sm text-gray-600 mb-4">{{ step.description }}</p>

              <div class="flex flex-wrap -mx-2">
                <template v-for="field in getStepVisibleFields(step)" :key="field.id || field.name">
                  <!-- Section Divider -->
                  <div v-if="field.type === 'section' || field.component === 'SectionDivider'" class="w-full mb-2 mt-6 px-2">
                    <div class="text-md font-normal">{{ field.label }}</div>
                    <hr class="mt-2" />
                  </div>

                  <!-- Campo -->
                  <div v-else :class="getFieldContainerClass(field)">
                    <div v-if="!isCheckboxField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field)" class="mb-2 text-sm">
                      {{ field.label }}
                      <span v-if="field.required" class="text-red-500">*</span>
                      <a
                        v-if="field.label_link_text && field.label_link_url"
                        :href="field.label_link_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ml-2 text-xs text-blue-600 underline hover:text-blue-800"
                      >{{ field.label_link_text }}</a>
                    </div>

                    <!-- Phone -->
                    <input v-if="isPhoneField(field)" type="tel" v-model="formData[field.name]" :placeholder="field.placeholder || '(00) 0000-0000'" v-maska:[phoneMaskOpts] :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- CPF -->
                    <input v-else-if="isCpfField(field)" type="text" v-model="formData[field.name]" :placeholder="field.placeholder || '000.000.000-00'" v-maska :data-maska="field.mask || '###.###.###-##'" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- CNPJ -->
                    <input v-else-if="isCnpjField(field)" type="text" v-model="formData[field.name]" :placeholder="field.placeholder || '00.000.000/0000-00'" v-maska :data-maska="field.mask || '##.###.###/####-##'" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- CEP -->
                    <input v-else-if="isCepField(field)" type="text" v-model="formData[field.name]" :placeholder="field.placeholder || '00000-000'" v-maska :data-maska="field.mask || '#####-###'" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @input="onFieldInput(field)" @blur="touchField(field.name)" />

                    <!-- Multiselect -->
                    <IMultiselect v-else-if="isMultiselectField(field)" v-model="formData[field.name]" :options="getFieldOptions(field)" :placeholder="field?.placeholder || 'Selecione'" :disabled="isFieldDisabled(field)" :filter="field.config?.searchable !== false" :error="hasFieldError(field)" @change="touchField(field.name)" />

                    <!-- Select (estilizado) -->
                    <ISelect v-else-if="isSelectField(field)" :modelValue="formData[field.name]" @update:modelValue="val => { formData[field.name] = val; touchField(field.name) }" :options="getFieldOptions(field)" :placeholder="field?.placeholder || 'Selecione'" :disabled="isFieldDisabled(field)" :error="hasFieldError(field)" />

                    <!-- Radio -->
                    <div v-else-if="isRadioField(field)" class="flex gap-4 flex-wrap">
                      <label v-for="opt in getFieldOptions(field)" :key="opt?.value || opt" class="flex items-center cursor-pointer">
                        <input type="radio" :name="field.name" :value="opt?.value || opt" v-model="formData[field.name]" class="mr-2 custom-radio" :class="{ 'border-red-500': hasFieldError(field) }" @change="touchField(field.name)" />
                        <span class="text-sm">{{ opt?.label || opt }}</span>
                      </label>
                    </div>

                    <!-- Checkbox / Terms -->
                    <label v-else-if="isCheckboxField(field)" class="flex items-start cursor-pointer">
                      <input type="checkbox" v-model="formData[field.name]" class="mr-2 mt-1 custom-checkbox" @change="touchField(field.name)" />
                      <span class="text-sm text-gray-700" v-html="renderCheckboxLabel(field)"></span>
                    </label>

                    <!-- File Upload -->
                    <div v-else-if="isFileField(field)" class="upload-box" @click="triggerFileInput(field.name)">
                      <input type="file" :ref="el => { if (el) fileRefs[field.name] = el }" class="hidden" :accept="field.config?.accept || '*/*'" @change="handleFileChange(field.name, $event)" />
                      <div v-if="!files[field.name]" class="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors">
                        <div class="flex justify-center mb-2">
                          <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
                        </div>
                        <p class="text-gray-600">Arraste e solte ou <span class="text-blue-500">clique para enviar</span></p>
                        <p class="text-sm text-gray-500 mt-1">{{ field.help_text || 'Anexe o arquivo solicitado' }}</p>
                      </div>
                      <div v-else class="text-center py-4 border-2 border-green-300 rounded-lg bg-green-50">
                        <p class="text-green-600">Arquivo selecionado: {{ files[field.name].name }}</p>
                        <span class="text-blue-500 cursor-pointer text-sm" @click.stop="clearFile(field.name)">Alterar</span>
                      </div>
                    </div>

                    <!-- Textarea -->
                    <textarea v-else-if="isTextareaField(field)" v-model="formData[field.name]" :placeholder="field.placeholder" :maxlength="getFieldMaxLength(field) || undefined" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" rows="3" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Date -->
                    <input v-else-if="isDateField(field)" type="text" inputmode="numeric" v-maska data-maska="##/##/####" placeholder="DD/MM/AAAA" v-model="formData[field.name]" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Date + Time -->
                    <input v-else-if="isDatetimeField(field)" type="text" inputmode="numeric" v-maska data-maska="##/##/#### ##:##" placeholder="DD/MM/AAAA HH:MM" v-model="formData[field.name]" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Terms Acceptance -->
                    <i-terms-acceptance
                      v-else-if="isTermsAcceptanceField(field)"
                      v-model="formData[field.name]"
                      :label="''"
                      :config="field.config || {}"
                      :hasError="hasFieldError(field)"
                      :errorMessage="getFieldErrorMessage(field)"
                      :disabled="isFieldDisabled(field)"
                      :required="field.required"
                    />

                    <!-- Fake Ticket (display-only) -->
                    <i-fake-ticket
                      v-else-if="isFakeTicketField(field)"
                      :config="field.config || {}"
                    />

                    <!-- Disclaimer (display-only) -->
                    <i-disclaimer
                      v-else-if="isDisclaimerField(field)"
                      :config="field.config || {}"
                    />

                    <!-- Instruções (display-only ou com aceite) -->
                    <i-instrucoes
                      v-else-if="isInstrucoesField(field)"
                      :modelValue="formData[field.name] === true"
                      @update:modelValue="val => { formData[field.name] = val; touchField(field.name); }"
                      :config="field.config || {}"
                      :hasError="hasFieldError(field)"
                    />

                    <!-- Default Text Input -->
                    <input v-else :type="getInputType(field)" v-model="formData[field.name]" :placeholder="field.placeholder" :maxlength="getFieldMaxLength(field) || undefined" v-maska :data-maska="field.mask || undefined" :disabled="isFieldDisabled(field)" :readonly="isFieldReadonly(field)" class="bg-white border border-gray-300 text-gray-900 text-sm rounded-md block w-full p-4 focus:outline-none focus:ring-1 focus:ring-gray-200" :class="{ 'border-red-500': hasFieldError(field), 'bg-gray-100': isFieldDisabled(field) || isFieldReadonly(field) }" @blur="touchField(field.name)" />

                    <!-- Error -->
                    <span v-if="hasFieldError(field) && !isTermsAcceptanceField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field)" class="text-red-500 text-xs mt-1 block">{{ getFieldErrorMessage(field) }}</span>

                    <!-- Help text -->
                    <p v-if="field.help_text && !isFileField(field) && !isCheckboxField(field) && !isTermsAcceptanceField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field) && !hasFieldError(field)" class="text-xs text-gray-500 mt-1">{{ field.help_text }}</p>
                  </div>
                </template>
              </div>
            </div>

            <!-- Etapa de Revisão -->
            <div v-if="isReviewStep" class="space-y-4">
              <p class="text-sm text-gray-600 mb-4">Revise os dados abaixo antes de enviar:</p>

              <div v-for="(step, stepIndex) in visibleSteps" :key="'review-' + (step?.id || stepIndex)" class="mb-6">
                <div class="border-b border-gray-200 pb-2 mb-3">
                  <h3 class="text-sm font-medium text-gray-700">{{ step?.name || 'Etapa ' + (stepIndex + 1) }}</h3>
                </div>

                <div class="space-y-2">
                  <template v-for="field in getStepVisibleFields(step)" :key="'review-field-' + (field?.name || field?.id)">
                    <!-- Campo normal -->
                    <div
                      v-if="field && !isFileField(field) && !isFakeTicketField(field) && !isDisclaimerField(field) && !isInstrucoesField(field) && field.type !== 'section' && (Array.isArray(formData[field.name]) ? formData[field.name].length > 0 : formData[field.name] !== '' && formData[field.name] !== null && formData[field.name] !== undefined)"
                      class="flex justify-between gap-3 py-2 border-b border-gray-100"
                    >
                      <span class="text-xs text-gray-500">{{ field.label }}</span>
                      <span class="text-sm text-gray-900 font-medium text-right">{{ getFieldDisplayValue(field) }}</span>
                    </div>

                    <!-- Arquivos -->
                    <div
                      v-else-if="field && isFileField(field) && files[field.name]"
                      class="flex justify-between gap-3 py-2 border-b border-gray-100"
                    >
                      <span class="text-xs text-gray-500">{{ field.label }}</span>
                      <span class="text-sm text-gray-900 font-medium text-right">{{ files[field.name].name }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="flex flex-col-reverse md:flex-row justify-between mt-8 gap-4">
              <button v-if="wizardStep > 1" type="button" @click="prevStep" class="bg-gray-500 w-full text-white px-4 py-2 rounded-md hover:bg-gray-600 disabled:opacity-50" :disabled="loading">
                Anterior
              </button>
              <button type="button" @click="nextStep" :disabled="isSubmitting || loading" class="button-subs w-full text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50">
                {{ wizardStep === totalSteps ? submitButtonText : 'Próximo' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>

    <!-- Sucesso -->
    <DynamicThankYouPage
      v-if="showThanks && thankYouPage"
      :thank-you-page="thankYouPage"
      :participant="participant"
    />
    <SuccessScreen
      v-if="showThanks && !thankYouPage"
      :title="participant.name + ', sua resposta foi enviada com sucesso!'"
      :message="'Obrigado por completar a ação solicitada.'"
      :participant="participant"
    />
  </main>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { vMaska } from 'maska'
import { validateFollowUpToken, submitFollowUpForm } from '@/services/api/followUp'
import Loading from 'vue-loading-overlay'
import DynamicThankYouPage from '@/components/dynamic-form/DynamicThankYouPage.vue'
import SuccessScreen from '@/components/dynamic-form/SuccessScreen.vue'
import ITermsAcceptance from '@/components/inputs/i-terms-acceptance.vue'
import IFakeTicket from '@/components/inputs/i-fake-ticket.vue'
import IDisclaimer from '@/components/inputs/i-disclaimer.vue'
import IInstrucoes from '@/components/inputs/i-instrucoes.vue'
import IMultiselect from '@/components/inputs/i-multiselect.vue'
import ISelect from '@/components/inputs/i-select.vue'
import { initializeTracking, getSourceDataForApi, trackConversion, cleanupTracking } from '@/utils/sourceTracker'
import { getFieldMaxLength } from '@/utils/validationBuilder'
import { shouldShowField, shouldShowStep } from '@/utils/conditionEvaluator'
import { executeBehaviors } from '@/utils/behaviorEngine'

// Lista de estados brasileiros — usada pelos campos de tipo `state` (espelha RSVPForm)
const brazilianStates = [
  { value: 'AC', label: 'Acre' }, { value: 'AL', label: 'Alagoas' }, { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' }, { value: 'BA', label: 'Bahia' }, { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' }, { value: 'ES', label: 'Espírito Santo' }, { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' }, { value: 'MT', label: 'Mato Grosso' }, { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' }, { value: 'PA', label: 'Pará' }, { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' }, { value: 'PE', label: 'Pernambuco' }, { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' }, { value: 'RN', label: 'Rio Grande do Norte' }, { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' }, { value: 'RR', label: 'Roraima' }, { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' }, { value: 'SE', label: 'Sergipe' }, { value: 'TO', label: 'Tocantins' }
]

export default {
  name: 'FollowUpForm',
  components: { Loading, DynamicThankYouPage, SuccessScreen, ITermsAcceptance, IFakeTicket, IDisclaimer, IInstrucoes, IMultiselect, ISelect },
  directives: { maska: vMaska },
  setup () {
    const route = useRoute()
    const token = computed(() => route.params.token || route.query['acao-retorno'])

    const loading = ref(false)
    const showForm = ref(false)
    const showThanks = ref(false)
    const error = ref(false)
    const errorTitle = ref('')
    const errorMessage = ref('')
    const isSubmitting = ref(false)
    const formSubmitted = ref(false)
    const touchedFields = ref({})
    const files = ref({})
    const fileRefs = ref({})
    const wizardStep = ref(1)

    const participant = ref({})
    const rule = ref({})
    const formSchema = ref({})
    const thankYouPage = ref(null)
    const formData = reactive({})

    // ---- Computed: steps e campos ----
    const steps = computed(() => formSchema.value?.steps || [])
    const isWizardMode = computed(() => formSchema.value?.is_wizard && steps.value.length > 1)

    const allFields = computed(() => {
      if (steps.value.length > 0) {
        return steps.value.flatMap(s => s.fields || [])
      }
      return formSchema.value?.fields || []
    })

    // Lista flat usada pelo conditionEvaluator para resolver field_id -> field.name
    const allSchemaFields = computed(() => allFields.value)

    const allVisibleFields = computed(() =>
      allFields.value.filter(f =>
        f && f.type !== 'hidden' && shouldShowField(f, formData, allSchemaFields.value)
      )
    )

    const visibleSteps = computed(() =>
      steps.value
        .filter(s => shouldShowStep(s, formData, allSchemaFields.value))
        .filter(s => (s.fields || []).some(f => f && f.type !== 'hidden'))
    )

    // Revisão habilitada se setting !== false (default true, consistente com RSVPForm)
    const reviewEnabled = computed(() => formSchema.value?.settings?.has_review_step !== false)

    // Lista combinada para navegação do wizard: steps de dados + step sintético de revisão
    const wizardSteps = computed(() => {
      const out = visibleSteps.value.slice()
      if (isWizardMode.value && reviewEnabled.value && out.length > 0) {
        out.push({ id: 'step_review', name: 'Revisão', isReviewStep: true, _isSynthetic: true })
      }
      return out
    })

    const totalSteps = computed(() => wizardSteps.value.length)
    const isReviewStep = computed(() => {
      if (!isWizardMode.value || !reviewEnabled.value) return false
      const idx = wizardStep.value - 1
      return !!(wizardSteps.value[idx]?.isReviewStep)
    })

    const submitButtonText = computed(() => formSchema.value?.submit_text || formSchema.value?.settings?.submit_text || 'Enviar')

    function getStepVisibleFields (step) {
      return (step.fields || []).filter(f =>
        f && f.type !== 'hidden' && shouldShowField(f, formData, allSchemaFields.value)
      )
    }

    // ---- Init ----
    onMounted(async () => {
      // Inicializa tracking completo (pageview + UTM + referrer)
      await initializeTracking();

      if (!token.value) return

      loading.value = true
      try {
        const resp = await validateFollowUpToken(token.value)

        if (!resp.ok) {
          error.value = true
          const titles = { not_found: 'Link inválido', expired: 'Link expirado', already_used: 'Ação já respondida' }
          errorTitle.value = titles[resp.error] || 'Erro'
          errorMessage.value = resp.message
          return
        }

        participant.value = resp.participant || {}
        rule.value = resp.rule || {}
        formSchema.value = resp.form || {}
        thankYouPage.value = resp.thank_you_page || null

        // Seed de defaults no formData — garante que toda key existe no objeto reactive
        // desde o primeiro render. Sem isso, v-model em select pode renderizar sem default
        // e is_empty no conditionEvaluator pode dar falso negativo no estado inicial.
        const seed = (fields) => {
          (fields || []).forEach(f => {
            if (f && f.name && !(f.name in formData)) {
              formData[f.name] = f.default !== undefined && f.default !== null ? f.default : ''
            }
          })
        }
        if (steps.value.length > 0) {
          steps.value.forEach(s => seed(s.fields))
        } else {
          seed(formSchema.value?.fields)
        }

        showForm.value = true
      } catch (err) {
        error.value = true
        errorTitle.value = 'Erro ao carregar formulário'
        errorMessage.value = err.response?.data?.message || 'Ocorreu um erro inesperado.'
      } finally {
        loading.value = false
      }
    })

    // ---- Wizard navigation ----
    function scrollToFormTop () {
      nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    }

    function nextStep () {
      formSubmitted.value = true

      // Na etapa de revisão, o botão "Enviar" submete.
      if (isReviewStep.value) {
        handleSubmit()
        return
      }

      if (isWizardMode.value) {
        const currentStepData = visibleSteps.value[wizardStep.value - 1]
        if (currentStepData && !validateStepFields(currentStepData)) {
          scrollToFormTop()
          return
        }
      }

      if (wizardStep.value === totalSteps.value) {
        // Sem step de revisão e está no último: submit direto.
        handleSubmit()
      } else {
        wizardStep.value++
        formSubmitted.value = false
        scrollToFormTop()
      }
    }

    function prevStep () {
      if (wizardStep.value > 1) {
        wizardStep.value--
        formSubmitted.value = false
      }
    }

    function validateStepFields (step) {
      // Só valida campos visíveis (respeitando field.conditions). Campos escondidos
      // por condition não devem bloquear o avanço, mesmo que required.
      const stepFields = getStepVisibleFields(step)
      const missing = stepFields.filter(f => {
        // Instruções com aceite — obrigatório por config, independente do flag `required`
        if (isInstrucoesAcceptanceField(f)) return formData[f.name] !== true
        if (!f.required) return false
        if (isFileField(f)) return !files.value[f.name]
        if (isCheckboxField(f) || isTermsAcceptanceField(f)) return formData[f.name] !== true
        return !formData[f.name] && formData[f.name] !== 0
      })
      return missing.length === 0
    }

    // ---- Submit ----
    async function handleSubmit () {
      formSubmitted.value = true

      // Required check só nos visíveis — esconder um campo required (via condition)
      // significa que ele não precisa ser preenchido.
      const missingRequired = allVisibleFields.value.filter(f => {
        if (isInstrucoesAcceptanceField(f)) return formData[f.name] !== true
        if (!f.required) return false
        if (isFileField(f)) return !files.value[f.name]
        if (isCheckboxField(f) || isTermsAcceptanceField(f)) return formData[f.name] !== true
        return !formData[f.name] && formData[f.name] !== 0
      })

      if (missingRequired.length > 0) return

      loading.value = true
      isSubmitting.value = true

      try {
        const fd = new FormData()

        // Só envia campos visíveis. Sem isso, valores digitados antes de uma condição
        // esconder o campo continuariam sendo persistidos no submit.
        const visibleNames = new Set(allVisibleFields.value.map(f => f.name))

        Object.keys(formData).forEach(key => {
          if (!visibleNames.has(key)) return
          let value = formData[key]
          // Converter DD/MM/YYYY para YYYY-MM-DD (formato esperado pela API)
          if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            const [dd, mm, yyyy] = value.split('/')
            value = `${yyyy}-${mm}-${dd}`
          }
          if (value !== null && value !== undefined && value !== '') {
            fd.append(key, value)
          }
        })

        Object.keys(files.value).forEach(key => {
          if (!visibleNames.has(key)) return
          if (files.value[key]) {
            fd.append(key, files.value[key])
          }
        })

        // Source Tracking (UTM + referrer)
        const sourceData = getSourceDataForApi();
        if (sourceData) {
          fd.append('source_tracking', JSON.stringify(sourceData));
        }

        await submitFollowUpForm(token.value, fd)
        showForm.value = false
        showThanks.value = true
      } catch (err) {
        error.value = true
        errorTitle.value = 'Erro ao enviar'
        errorMessage.value = err.response?.data?.message || 'Erro ao enviar formulário.'
      } finally {
        loading.value = false
        isSubmitting.value = false
      }
    }

    // ---- Field type helpers (espelhados do RSVPForm) ----
    // Tipos explícitos que NÃO podem ser inferidos como phone/cpf/cnpj/cep pelo fallback de nome
    const EXPLICIT_TYPES = ['select', 'dropdown', 'multiselect', 'radio', 'checkbox',
      'gender', 'state', 'autocomplete', 'textarea', 'date', 'birthdate', 'datetime',
      'file', 'image', 'terms', 'terms_acceptance', 'toggle', 'checkbox_single',
      'fake_ticket', 'disclaimer']

    const phoneMaskOpts = { mask: ['(##) ####-####', '(##) #####-####'] }

    const isPhoneField = (f) => {
      if (!f) return false
      const t = f.type || f.fieldType?.name || f.config?.type
      if (t === 'phone' || t === 'tel' || t === 'telefone') return true
      if (EXPLICIT_TYPES.includes(t)) return false
      return !!(f.name && (f.name.includes('phone') || f.name.includes('telefone')))
    }
    const isCpfField = (f) => {
      if (!f) return false
      const t = f.type || f.fieldType?.name || f.config?.type
      if (t === 'cpf') return true
      if (EXPLICIT_TYPES.includes(t)) return false
      return !!(f.name && f.name.includes('cpf') && !f.name.includes('cnpj'))
    }
    const isCnpjField = (f) => {
      if (!f) return false
      const t = f.type || f.fieldType?.name || f.config?.type
      if (t === 'cnpj' || t === 'cpf_cnpj' || t === 'document') return true
      if (EXPLICIT_TYPES.includes(t)) return false
      return !!(f.name && f.name.includes('cnpj'))
    }
    const isCepField = (f) => {
      if (!f) return false
      const t = f.type || f.fieldType?.name || f.config?.type
      if (t === 'cep') return true
      if (EXPLICIT_TYPES.includes(t)) return false
      return !!(f.name && f.name.includes('cep'))
    }
    const isMultiselectField = (f) => {
      if (!f) return false
      return f.type === 'multiselect' || f.component === 'MultiselectField'
    }
    const isSelectField = (f) => {
      if (!f) return false
      if (isMultiselectField(f)) return false
      return ['select', 'dropdown', 'gender', 'state'].includes(f.type) || ['SelectField', 'StateField'].includes(f.component)
    }
    const isRadioField = (f) => f && (f.type === 'radio' || f.component === 'RadioField')
    const isTermsAcceptanceField = (f) => {
      if (!f) return false
      return f.type === 'terms_acceptance' || f.component === 'TermsAcceptanceField' || f.fieldType?.name === 'terms_acceptance'
    }
    const isFakeTicketField = (f) => {
      if (!f) return false
      return f.type === 'fake_ticket' ||
             f.component === 'FakeTicketField' ||
             f.fieldType?.name === 'fake_ticket' ||
             f.field_type?.name === 'fake_ticket' ||
             f.config?.type === 'fake_ticket'
    }
    const isInstrucoesField = (f) => {
      if (!f) return false
      return f.type === 'instrucoes' ||
             f.component === 'InstrucoesField' ||
             f.fieldType?.name === 'instrucoes' ||
             f.field_type?.name === 'instrucoes' ||
             f.config?.type === 'instrucoes'
    }
    const isInstrucoesAcceptanceField = (f) => {
      return isInstrucoesField(f) && !!(f.config && f.config.require_acceptance)
    }
    const isDisclaimerField = (f) => {
      if (!f) return false
      return f.type === 'disclaimer' ||
             f.component === 'DisclaimerField' ||
             f.fieldType?.name === 'disclaimer' ||
             f.field_type?.name === 'disclaimer' ||
             f.config?.type === 'disclaimer'
    }
    const isCheckboxField = (f) => {
      if (!f) return false
      if (isTermsAcceptanceField(f)) return false
      return ['checkbox', 'checkbox_single', 'toggle', 'terms'].includes(f.type) || ['CheckboxField', 'TermsField'].includes(f.component)
    }

    // Resolve options de um field, com tratamento especial pra state (brazilianStates)
    const getFieldOptions = (f) => {
      if (!f) return []
      if (f.type === 'state' || f.component === 'StateField' || f.name === 'state') {
        return brazilianStates
      }
      return f.options || []
    }

    // Flags do schema — espelhadas do RSVPForm
    const isFieldDisabled = (f) => {
      if (!f) return false
      return !!(f.disabled === true || f.config?.disabled === true)
    }
    const isFieldReadonly = (f) => {
      if (!f) return false
      return !!(f.readonly === true || f.config?.readonly === true)
    }

    // Behavior engine — invoca side effects declarativos (ex: CEP → viaCEP autofill)
    const onFieldInput = (f) => {
      if (!f?.behaviors || !Array.isArray(f.behaviors) || f.behaviors.length === 0) return
      const value = formData[f.name]
      const fieldsForBehavior = allSchemaFields.value
      executeBehaviors(f, value, fieldsForBehavior, (name, val) => {
        formData[name] = val
      })
    }

    // Formata um valor para exibição na etapa de revisão (espelha RSVPForm)
    const getFieldDisplayValue = (f) => {
      if (!f || !f.name) return ''
      const value = formData[f.name]
      if (value === undefined || value === null || value === '') return '-'

      if (isCheckboxField(f) || isTermsAcceptanceField(f)) {
        return value === true ? 'Sim' : 'Não'
      }
      if (isMultiselectField(f) && Array.isArray(value)) {
        const options = getFieldOptions(f)
        return value.map(v => {
          const opt = options.find(o => o.value === v)
          return opt?.label || v
        }).join(', ')
      }
      if (isSelectField(f) || isRadioField(f)) {
        const options = getFieldOptions(f)
        const opt = options.find(o => o.value === value)
        return opt?.label || value
      }
      if (isDateField(f)) return value
      return value
    }
    const hasTermsLink = (field) => {
      return field?.config?.link_text && field?.config?.link_url
    }
    const getCheckboxLabel = (field) => {
      return field?.label || field?.options?.[0]?.label || field?.help_text || ''
    }
    const renderCheckboxLabel = (field) => {
      const label = getCheckboxLabel(field)
      const requiredMark = field?.required ? ' <span style="color: #ef4444;">*</span>' : ''
      if (!hasTermsLink(field)) {
        return label + requiredMark
      }
      const linkText = field.config.link_text
      const linkUrl = field.config.link_url
      const escaped = linkText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(escaped, 'i')
      const match = label.match(regex)
      if (match) {
        return label.replace(regex, `<a href="${linkUrl}" target="_blank" rel="noopener" onclick="event.stopPropagation()" style="color: #3b82f6; text-decoration: underline;">${match[0]}</a>`) + requiredMark
      }
      return `${label} <a href="${linkUrl}" target="_blank" rel="noopener" onclick="event.stopPropagation()" style="color: #3b82f6; text-decoration: underline;">${linkText}</a>${requiredMark}`
    }
    const isFileField = (f) => f && (['file', 'image'].includes(f.type) || ['FileField', 'ImageField'].includes(f.component))
    const isTextareaField = (f) => f && f.type !== 'email' && (f.type === 'textarea' || f.component === 'TextareaField')
    const isDateField = (f) => f && ['date', 'birthdate'].includes(f.type)
    const isDatetimeField = (f) => {
      if (!f) return false
      return f.type === 'datetime' || f.component === 'DatetimeField'
    }

    const getInputType = (f) => {
      if (!f) return 'text'
      const map = { email: 'email', number: 'number', url: 'url', password: 'password' }
      return map[f.type] || 'text'
    }

    const getFieldContainerClass = (f) => {
      if (!f) return 'w-full mb-4 px-2'
      const cols = String(f.layout?.cols || '12')
      const map = {
        '3': 'w-full md:w-1/4 mb-4 px-2', '4': 'w-full md:w-1/3 mb-4 px-2',
        '6': 'w-full md:w-1/2 mb-4 px-2', '8': 'w-full md:w-2/3 mb-4 px-2',
        '9': 'w-full md:w-3/4 mb-4 px-2', '12': 'w-full mb-4 px-2'
      }
      return map[cols] || 'w-full mb-4 px-2'
    }

    const touchField = (name) => { touchedFields.value[name] = true }

    // Valida se string DD/MM/YYYY é uma data real
    const isValidDate = (value) => {
      if (!value) return true
      const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
      if (!match) return false
      const [, dd, mm, yyyy] = match
      const d = new Date(+yyyy, +mm - 1, +dd)
      return d.getFullYear() === +yyyy && d.getMonth() === +mm - 1 && d.getDate() === +dd
    }

    // Exige data + hora completas e válidas (DD/MM/AAAA HH:MM)
    const isValidDatetime = (value) => {
      if (!value) return true
      const m = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/)
      if (!m) return false
      const [, dd, mm, yyyy, hh, min] = m
      const d = new Date(+yyyy, +mm - 1, +dd)
      const dateOk = d.getFullYear() === +yyyy && d.getMonth() === +mm - 1 && d.getDate() === +dd
      const timeOk = +hh <= 23 && +min <= 59
      return dateOk && timeOk
    }

    // Valida campo de data (retorna mensagem de erro ou null)
    const getDateError = (f) => {
      const value = formData[f.name]
      if (!value) return null
      if (!isValidDate(value)) return 'Data inválida'
      if (f.type === 'birthdate' || f.type === 'birth_date') {
        const [dd, mm, yyyy] = value.split('/')
        const date = new Date(+yyyy, +mm - 1, +dd)
        if (date >= new Date()) return 'Data de nascimento deve ser anterior a hoje'
        const validations = Array.isArray(f.validations) ? f.validations : []
        const minAgeRule = validations.find(v => v.rule === 'min_age')
        if (minAgeRule) {
          const minYears = minAgeRule.years || 18
          const today = new Date()
          let age = today.getFullYear() - date.getFullYear()
          const mDiff = today.getMonth() - date.getMonth()
          if (mDiff < 0 || (mDiff === 0 && today.getDate() < date.getDate())) age--
          if (age < minYears) return `Idade mínima: ${minYears} anos`
        }
      }
      return null
    }

    const hasFieldError = (f) => {
      if (!f) return false
      if (!formSubmitted.value && !touchedFields.value[f.name]) return false
      // Validação de data (independente de required)
      if (isDateField(f) && formData[f.name] && getDateError(f)) return true
      // Validação de data e hora — bloqueia valor incompleto (ex: só a data)
      if (isDatetimeField(f) && formData[f.name] && !isValidDatetime(formData[f.name])) return true
      // Instruções com aceite — obrigatório por config, independente do flag `required`
      if (isInstrucoesAcceptanceField(f)) return formData[f.name] !== true
      if (!f.required) return false
      if (isFileField(f)) return !files.value[f.name]
      if (isCheckboxField(f) || isTermsAcceptanceField(f)) return formData[f.name] !== true
      return !formData[f.name] && formData[f.name] !== 0
    }

    const getFieldErrorMessage = (f) => {
      if (isDateField(f) && formData[f.name]) {
        const dateErr = getDateError(f)
        if (dateErr) return dateErr
      }
      if (isDatetimeField(f) && formData[f.name] && !isValidDatetime(formData[f.name])) {
        return 'Informe data e hora completas (DD/MM/AAAA HH:MM).'
      }
      if (isInstrucoesAcceptanceField(f)) return 'Você precisa marcar o aceite das instruções para prosseguir.'
      if (isFileField(f)) return 'Por favor, anexe o arquivo solicitado.'
      if (isTermsAcceptanceField(f)) return 'Você deve ler e aceitar os termos para prosseguir.'
      if (isCheckboxField(f)) return 'Este campo é obrigatório.'
      return 'Campo obrigatório.'
    }

    // ---- File handling ----
    const triggerFileInput = (name) => {
      if (fileRefs.value[name]) fileRefs.value[name].click()
    }
    const handleFileChange = (name, event) => {
      const file = event.target.files[0]
      if (!file) return

      // Busca max_size: prioriza validations (form builder), depois config, depois default 20MB
      const field = allVisibleFields.value?.find(f => f.name === name)
      const validations = Array.isArray(field?.validations) ? field.validations : []
      const fileSizeRule = validations.filter(v => v.rule === 'file_size').pop()
      const maxSizeKb = fileSizeRule?.max_kb || 20480
      if (file.size > maxSizeKb * 1024) {
        const maxMb = (maxSizeKb / 1024).toFixed(0)
        errorMessage.value = `O arquivo "${file.name}" excede o tamanho máximo de ${maxMb}MB.`
        error.value = true
        event.target.value = ''
        return
      }

      files.value[name] = file
    }
    const clearFile = (name) => {
      files.value[name] = null
      if (fileRefs.value[name]) fileRefs.value[name].value = ''
    }

    return {
      loading, showForm, showThanks, error, errorTitle, errorMessage,
      isSubmitting, formSubmitted, formData, files, fileRefs, touchedFields,
      wizardStep, participant, rule, formSchema, thankYouPage,
      isWizardMode, allVisibleFields, visibleSteps, wizardSteps, totalSteps, submitButtonText,
      reviewEnabled, isReviewStep, getFieldDisplayValue,
      getStepVisibleFields, nextStep, prevStep, handleSubmit, touchField,
      isPhoneField, phoneMaskOpts, isCpfField, isCnpjField, isCepField, isMultiselectField,
      isSelectField, isRadioField, isCheckboxField, getFieldOptions,
      isFileField, isTextareaField, isDateField, isDatetimeField, isTermsAcceptanceField, isFakeTicketField, isDisclaimerField, isInstrucoesField, isInstrucoesAcceptanceField,
      isFieldDisabled, isFieldReadonly, onFieldInput,
      hasTermsLink, renderCheckboxLabel,
      getInputType, getFieldMaxLength, getFieldContainerClass, hasFieldError, getFieldErrorMessage,
      triggerFileInput, handleFileChange, clearFile
    }
  }
}
</script>

<style scoped>
/* Mobile: prevent iOS auto-zoom on input focus */
@media screen and (max-width: 767px) {
  input,
  select,
  textarea {
    font-size: 16px !important;
  }
}

.button-subs {
  background-color: var(--button-color, #556ee6);
}
.button-subs:hover {
  background-color: var(--button-hover-color, #4559c9);
}
</style>
